import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HarmonyLogoCompact } from './HarmonyLogo';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { LogOut, User, Settings, Shield, ChevronDown, Home, Eye, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  it_admin: 'IT Admin',
  hr_admin: 'HR Admin',
  viewer: 'Viewer'
};

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin, isImpersonating, originalUser, stopImpersonation } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    const result = await stopImpersonation();
    if (result.success) {
      toast.success('Returned to your admin session');
      navigate('/admin/users');
    } else {
      toast.error(result.error || 'Failed to stop impersonation');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="main-layout">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div 
          className="bg-amber-500 text-amber-950 px-4 py-2 text-center relative z-[60]"
          data-testid="impersonation-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <Eye className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium">
              Viewing as <strong>{user?.full_name || user?.username}</strong> ({ROLE_LABELS[user?.role] || user?.role})
              {originalUser && <span className="opacity-75"> &mdash; Logged in as {originalUser.full_name || originalUser.username}</span>}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleStopImpersonation}
              className="h-7 px-3 bg-amber-950/10 border-amber-950/20 text-amber-950 hover:bg-amber-950/20 hover:text-amber-950 gap-1.5"
              data-testid="stop-impersonation-btn"
            >
              <X className="w-3 h-3" />
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-border nav-shadow" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate('/dashboard')}
              data-testid="logo-link"
            >
              <HarmonyLogoCompact size={36} />
              <span className="font-semibold text-lg text-foreground hidden sm:block">
                Harmony Apps
              </span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-6">
              <Button
                variant={location.pathname === '/dashboard' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="gap-2"
                data-testid="nav-dashboard"
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Button>
              
              {isSuperAdmin && (
                <Button
                  variant={location.pathname.startsWith('/admin') ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/admin/users')}
                  className="gap-2"
                  data-testid="nav-admin"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Button>
              )}
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 px-2"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className={`text-sm ${isImpersonating ? 'bg-amber-500 text-amber-950' : 'bg-primary text-primary-foreground'}`}>
                      {getInitials(user?.full_name || user?.username)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:flex flex-col items-start">
                    <span className="text-sm font-medium">{user?.full_name || user?.username}</span>
                    <span className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-muted-foreground">{user?.email || 'No email'}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isImpersonating ? (
                  <DropdownMenuItem 
                    onClick={handleStopImpersonation}
                    className="cursor-pointer text-amber-600 focus:text-amber-700"
                    data-testid="menu-stop-impersonation"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Exit Impersonation
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer"
                      data-testid="menu-profile"
                    >
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    {isSuperAdmin && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/users')}
                        className="cursor-pointer md:hidden"
                        data-testid="menu-admin-mobile"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer text-destructive focus:text-destructive"
                      data-testid="menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {children}
      </main>
    </div>
  );
};

export default Layout;
